import { useQuery } from '@tanstack/react-query'
import backend from '@api/backend'
import {
  Group,
  Permission,
  Role,
} from '@data/users/model/user'

export const ROLES_QUERY_KEY = ['roles'] as const
export const PERMISSIONS_QUERY_KEY = ['permissions'] as const
export const GROUPS_QUERY_KEY = ['groups'] as const

interface RbacItemResponse {
  id: number | string
  name: string
  description: string
}

interface RoleResponse extends RbacItemResponse {
  permissions: RbacItemResponse[]
}

interface GroupResponse extends RbacItemResponse {
  roles: RbacItemResponse[]
}

const mapPermission = (permission: RbacItemResponse): Permission => ({
  id: String(permission.id),
  name: permission.name,
  description: permission.description,
})

const mapRole = (role: RoleResponse): Role => ({
  ...mapPermission(role),
  permissions: role.permissions.map(mapPermission),
})

const mapGroup = (group: GroupResponse): Group => ({
  ...mapPermission(group),
  roles: group.roles.map((role) => ({
    ...mapPermission(role),
    permissions: [],
  })),
})

export const useRolesQuery = () =>
  useQuery<Role[]>({
    queryKey: ROLES_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<RoleResponse[]>('/roles', { signal })
      return response.data.map(mapRole)
    },
    staleTime: Infinity,
  })

export const usePermissionsQuery = () =>
  useQuery<Permission[]>({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<RbacItemResponse[]>('/permissions', { signal })
      return response.data.map(mapPermission)
    },
    staleTime: Infinity,
  })

export const useGroupsQuery = () =>
  useQuery<Group[]>({
    queryKey: GROUPS_QUERY_KEY,
    queryFn: async ({ signal }) => {
      const response = await backend.get<GroupResponse[]>('/groups', { signal })
      return response.data.map(mapGroup)
    },
    staleTime: Infinity,
  })
